
import { withDevelopmentEndpointReload } from '~/shared/store/api-services/with-development-endpoint-reload';
import type { Action, PayloadAction } from '@reduxjs/toolkit';
import {
  type BaseQueryFn,
  createApi,
  type FetchArgs,
  type FetchBaseQueryError,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import { HYDRATE } from 'next-redux-wrapper';
import { toast } from 'react-toastify';

import FlashMessage from '~/shared/components/flash-message';
import { API_HOST, LOCALES } from '~/shared/constants';
import { isBrowser } from '~/shared/helpers/browser-helpers';
import {
  FetchGraphQLError,
  getLocalizedHttpError,
  parseGraphQLOperation,
  reportRumError,
} from '~/shared/helpers/graphql-error-helpers';
import { processGraphQLResponse, toGraphQLResponse } from '~/shared/helpers/process-graphql-response';
import { createClientRequestId } from '~/shared/libs/observability/client-request-id';
import { calendarTagTypes, getCalendarQuery } from '~/shared/store/api-services/calendar/calendar-slice';
import { categoriesTagTypes, getCategoriesQuery } from '~/shared/store/slices/categories/categories-query';
import { clientWorkspaceTagTypes, getClientWorkspaceQuery } from '~/shared/store/slices/client-workspace';
import { companiesTagTypes, getCompaniesQuery } from '~/shared/store/slices/companies/companies-query';
import { consultationTagTypes, getConsultationsQuery } from '~/shared/store/slices/consultations/consultations-query';
import {
  courseCategoriesTagTypes,
  getCourseCategoriesQuery,
} from '~/shared/store/slices/course-categories/course-categories-query';
import {
  courseMetricDefinitionsTagTypes,
  getCourseMetricDefinitionsQuery,
} from '~/shared/store/slices/course-metric-definitions/course-metric-definitions-query';
import { coursesTagTypes, getCoursesQuery } from '~/shared/store/slices/courses/courses-query';
import { dialogsTagTypes, getDialogsQuery } from '~/shared/store/slices/dialogs/dialogs-query';
import { filesTagTypes, getFilesQuery } from '~/shared/store/slices/files/files-query';
import {
  formTemplatesTagTypes,
  getFormTemplatesQuery,
} from '~/shared/store/slices/form-templates/form-templates-query';
import { getHomeTasksQuery, homeTasksTagTypes } from '~/shared/store/slices/home-tasks/home-tasks-query';
import {
  getLessonExercisesQuery,
  lessonExercisesTagTypes,
} from '~/shared/store/slices/lesson-exercises/lesson-exercises-query';
import {
  getLessonTemplatesQuery,
  lessonTemplatesTagTypes,
} from '~/shared/store/slices/lesson-templates/lesson-templates-query';
import { getLessonsQuery, lessonsTagTypes } from '~/shared/store/slices/lessons/lessons-query';
import { getMessagesQuery, messagesTagTypes } from '~/shared/store/slices/messages/messages-query';
import { getMetricValuesQuery, metricValuesTagTypes } from '~/shared/store/slices/metric-values/metric-values-query';
import { getPostsQuery, postsTagTypes } from '~/shared/store/slices/posts/posts-query';
import { getTagsQuery, tagsTagTypes } from '~/shared/store/slices/tags/tags-query';
import { getUserGroupsQuery, userGroupsTagTypes } from '~/shared/store/slices/user-groups/user-groups-query';
import { getUsersQuery, usersTagTypes } from '~/shared/store/slices/users/users-query';
import { getVotesQuery, votesTagTypes } from '~/shared/store/slices/votes/votes-query';

type ExtendedFetchArgs = FetchArgs & {
  meta?: {
    hideFlashMessage?: boolean;
  };
};

function pickSuccessNode(graphqlPayload: unknown, opName?: string): unknown {
  const data =
    typeof graphqlPayload === 'object' && graphqlPayload !== null && 'data' in graphqlPayload
      ? (graphqlPayload as { data?: Record<string, unknown> }).data
      : undefined;

  if (!data || typeof data !== 'object') {
    return null;
  }

  if (opName && opName in data) {
    return data[opName];
  }

  const firstKey = Object.keys(data)[0];

  return firstKey ? data[firstKey] : null;
}

const createRawBaseQuery = (baseUrl: string) =>
  fetchBaseQuery({
    credentials: 'include',
    baseUrl,
    prepareHeaders: async (headers, { getState }) => {
      const state = getState() as { app?: { lng?: string } };
      const language = state.app?.lng || LOCALES.UA;

      headers.set('Content-Type', 'application/json');
      headers.set('Accept-Language', language);

      return headers;
    },
  });

const browserBaseQuery = createRawBaseQuery('/api/graphql');

const getRawBaseQuery = () => {
  if (isBrowser()) {
    const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    if (process.env.NEXT_PUBLIC_LOCAL_API === 'true' || isLocalHost) {
      const localApiHost = `${window.location.protocol}//${window.location.hostname}:3002`;

      return createRawBaseQuery(`${localApiHost}/api/graphql`);
    }

    return browserBaseQuery;
  }

  const publicAppUrl = process.env.NEXT_PUBLIC_APP_URL?.replace(/\/+$/, '') || API_HOST;

  return createRawBaseQuery(`${publicAppUrl}/api/graphql`);
};

const isProduction = process.env.NODE_ENV === 'production';

const shouldShowTechnicalError = !isProduction;

export const baseQueryWithToasts: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extra,
) => {
  let body: unknown;
  let hideToasts = false;

  try {
    const originalRequest =
      typeof args === 'string' ? ({ url: args } as ExtendedFetchArgs) : (args as ExtendedFetchArgs);

    hideToasts = originalRequest.meta?.hideFlashMessage ?? false;
    body = typeof originalRequest.body === 'string' ? JSON.parse(originalRequest.body) : originalRequest.body;
  } catch {
    body = undefined;
  }

  const rawQuery =
    typeof body === 'object' && body !== null && 'query' in body && typeof body.query === 'string' ? body.query : '';

  const variables =
    typeof body === 'object' && body !== null && 'variables' in body
      ? (body as { variables?: unknown }).variables
      : undefined;

  const { opType, opName } = parseGraphQLOperation(rawQuery);

  const request: FetchArgs = typeof args === 'string' ? { url: args } : { ...(args as FetchArgs) };

  const clientRequestId = isBrowser() ? createClientRequestId() : undefined;

  if (clientRequestId) {
    request.headers = {
      ...(request.headers as Record<string, string> | undefined),
      'x-client-request-id': clientRequestId,
    };
  }

  const result = await getRawBaseQuery()(request, api, extra);

  if ('error' in result && result.error) {
    const error = result.error as FetchBaseQueryError & {
      data?: unknown;
    };

    try {
      const gqlPayload = toGraphQLResponse(error.data);

      if (gqlPayload.errors?.length) {
        await processGraphQLResponse({
          payload: gqlPayload,
          source: 'rtk',
          operationName: opName,
          clientRequestId,
          variables,
          opType,
          httpStatus: String(error.status ?? ''),
        });
      }

      const localizedHttpError = await getLocalizedHttpError({
        status: error.status,
      });

      if (!hideToasts && isBrowser() && shouldShowTechnicalError) {
        toast.error(<FlashMessage title={localizedHttpError.title} message={localizedHttpError.message} />);
      }

      reportRumError({
        source: 'rtk',
        operationName: opName,
        message: `HTTP ${String(error.status ?? 'unknown')}`,
        clientRequestId,
        variables,
        extra: {
          opType: opType || '',
          httpStatus: String(error.status ?? ''),
          rawErrorData: error.data,
        },
      });
    } catch (caughtError) {
      if (
        !hideToasts &&
        isBrowser() &&
        caughtError instanceof FetchGraphQLError &&
        caughtError.title &&
        !caughtError.isSystemGraphQLError
      ) {
        toast.error(<FlashMessage title={caughtError.title} message={caughtError.message} />);
      }
    }

    return result;
  }

  const payload =
    typeof result === 'object' && result !== null && 'data' in result ? (result as { data?: unknown }).data : undefined;

  try {
    await processGraphQLResponse({
      payload: toGraphQLResponse(payload),
      source: 'rtk',
      operationName: opName,
      clientRequestId,
      variables,
      opType,
    });
  } catch (caughtError) {
    if (
      !hideToasts &&
      isBrowser() &&
      caughtError instanceof FetchGraphQLError &&
      caughtError.title &&
      !caughtError.isSystemGraphQLError
    ) {
      toast.error(<FlashMessage title={caughtError.title} message={caughtError.message} />);
    }
  }

  const gqlPayload = toGraphQLResponse(payload);

  if (!gqlPayload.errors?.length && opType === 'mutation' && isBrowser() && !hideToasts) {
    try {
      const node = pickSuccessNode(payload, opName) as {
        message?: string;
        title?: string;
      } | null;

      const message = node?.message;
      const title = node?.title ?? 'Success';

      if (message) {
        toast.success(<FlashMessage title={title} message={message} />);
      }
    } catch {}
  }

  return result;
};

type HydratePayload = Record<string, unknown>;

function isHydrateAction(action: Action): action is PayloadAction<HydratePayload> {
  return action.type === HYDRATE;
}

export const graphqlApi = withDevelopmentEndpointReload(createApi({
  reducerPath: 'graphql',
  baseQuery: baseQueryWithToasts,
  extractRehydrationInfo(action, { reducerPath }) {
    if (!isHydrateAction(action)) {
      return undefined;
    }

    return action.payload[reducerPath] as never;
  },
  tagTypes: [
    ...usersTagTypes,
    ...clientWorkspaceTagTypes,
    ...votesTagTypes,
    ...calendarTagTypes,
    ...coursesTagTypes,
    ...tagsTagTypes,
    ...courseCategoriesTagTypes,
    ...lessonTemplatesTagTypes,
    ...lessonsTagTypes,
    ...lessonExercisesTagTypes,
    ...filesTagTypes,
    ...postsTagTypes,
    ...formTemplatesTagTypes,
    ...homeTasksTagTypes,
    ...dialogsTagTypes,
    ...messagesTagTypes,
    ...userGroupsTagTypes,
    ...companiesTagTypes,
    ...categoriesTagTypes,
    ...consultationTagTypes,
    ...courseMetricDefinitionsTagTypes,
    ...metricValuesTagTypes,
  ],
  endpoints: (builder) => {
    const usersEndpoints = getUsersQuery(builder);
    const clientWorkspaceEndpoints = getClientWorkspaceQuery(builder);
    const votesEndpoints = getVotesQuery(builder);
    const calendarEndpoints = getCalendarQuery(builder);
    const coursesEndpoints = getCoursesQuery(builder);
    const tagsEndpoints = getTagsQuery(builder);
    const courseCategoriesEndpoints = getCourseCategoriesQuery(builder);
    const lessonTemplatesEndpoints = getLessonTemplatesQuery(builder);
    const lessonsEndpoints = getLessonsQuery(builder);
    const lessonExercisesEndpoints = getLessonExercisesQuery(builder);
    const filesEndpoints = getFilesQuery(builder);
    const postsEndpoints = getPostsQuery(builder);
    const formTemplatesEndpoints = getFormTemplatesQuery(builder);
    const homeTasksEndpoints = getHomeTasksQuery(builder);
    const dialogsEndpoints = getDialogsQuery(builder);
    const messagesEndpoints = getMessagesQuery(builder);
    const userGroupsEndpoints = getUserGroupsQuery(builder);
    const companiesEndpoints = getCompaniesQuery(builder);
    const categoriesEndpoints = getCategoriesQuery(builder);
    const consultationsEndpoints = getConsultationsQuery(builder);
    const courseMetricDefinitionsEndpoints = getCourseMetricDefinitionsQuery(builder);
    const metricValuesEndpoints = getMetricValuesQuery(builder);

    return {
      ...usersEndpoints,
      ...votesEndpoints,
      ...calendarEndpoints,
      ...coursesEndpoints,
      ...tagsEndpoints,
      ...courseCategoriesEndpoints,
      ...clientWorkspaceEndpoints,
      ...lessonTemplatesEndpoints,
      ...lessonsEndpoints,
      ...lessonExercisesEndpoints,
      ...filesEndpoints,
      ...postsEndpoints,
      ...formTemplatesEndpoints,
      ...homeTasksEndpoints,
      ...dialogsEndpoints,
      ...messagesEndpoints,
      ...userGroupsEndpoints,
      ...companiesEndpoints,
      ...categoriesEndpoints,
      ...consultationsEndpoints,
      ...courseMetricDefinitionsEndpoints,
      ...metricValuesEndpoints,
    };
  },
}));
